/**
 * ContentOverlay — hover/tap overlay with Copy + Expand buttons.
 * Appears on images, diagrams, tables, and math blocks.
 * Desktop: 200ms hover delay, show pill buttons top-right.
 * Mobile: tap-to-reveal with 3s auto-dismiss, scroll-dismiss.
 *
 * Ported from mojoDoc and extracted into its own component file
 * to keep markdown-renderer.tsx focused on rendering logic.
 */

import React, { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { Copy, Check, Maximize2 } from "lucide-react";

export type ContentOverlayType = "table" | "diagram" | "image" | "math";

interface ContentOverlayProps {
  type: ContentOverlayType;
  onCopy: () => Promise<void>;
  onExpand: () => void;
  copyLabel?: string;
  children: ReactNode;
}

export function ContentOverlay({
  type,
  onCopy,
  onExpand,
  copyLabel,
  children,
}: ContentOverlayProps) {
  const [copied, setCopied] = useState(false);
  const [showMobileOverlay, setShowMobileOverlay] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track mobile vs desktop
  useEffect(() => {
    const check = () => setIsMobile(!window.matchMedia("(hover: hover)").matches);
    check();
    const mql = window.matchMedia("(hover: hover)");
    mql.addEventListener("change", check);
    return () => mql.removeEventListener("change", check);
  }, []);

  // Clean up timers
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
    };
  }, []);

  // Clear mobile overlay on scroll
  useEffect(() => {
    if (!showMobileOverlay || !isMobile) return;
    const handleScroll = () => {
      setShowMobileOverlay(false);
      if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showMobileOverlay, isMobile]);

  const handleMouseEnter = useCallback(() => {
    // 200ms delay before showing overlay on desktop
    hoverTimerRef.current = setTimeout(() => setHovered(true), 200);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHovered(false);
  }, []);

  // Mobile: tap to reveal with 3s auto-dismiss
  const handleTap = useCallback(() => {
    if (!isMobile) return;
    if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
    setShowMobileOverlay(true);
    mobileTimerRef.current = setTimeout(() => {
      setShowMobileOverlay(false);
    }, 3000);
  }, [isMobile]);

  const handleCopyClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("mojoReader: Content overlay copy failed", err);
    }
  }, [onCopy]);

  const handleExpandClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onExpand();
  }, [onExpand]);

  const copyTitle = copyLabel || (
    type === "table" ? "Copy as TSV" :
    type === "diagram" ? "Copy as PNG" :
    type === "image" ? "Copy image" :
    type === "math" ? "Copy LaTeX" :
    "Copy"
  );

  const isVisible = hovered || showMobileOverlay;

  return (
    <span
      className="content-overlay-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={isMobile ? handleTap : undefined}
    >
      {children}
      <span className={`content-overlay-actions${isVisible ? " visible" : ""}`}>
        <button
          className={`content-overlay-btn${copied ? " copied" : ""}`}
          onClick={handleCopyClick}
          title={copied ? "Copied!" : copyTitle}
          aria-label={copied ? "Copied to clipboard" : copyTitle}
        >
          {copied
            ? <Check className="content-overlay-icon" />
            : <Copy className="content-overlay-icon" />
          }
        </button>
        <button
          className="content-overlay-btn"
          onClick={handleExpandClick}
          title="Expand"
          aria-label="Expand in viewer"
        >
          <Maximize2 className="content-overlay-icon" />
        </button>
      </span>
    </span>
  );
}
